import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import { Download, Share2, X, Loader2, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Betohalics-branded, saveable/shareable payout or deposit receipt.
 * Renders a polished dark card with the crown logo and captures it to a
 * PNG (html-to-image) for download / native share.
 */
const BrandedReceipt = ({ open, onClose, receipt }) => {
    const cardRef = useRef(null);
    const [busy, setBusy] = useState(false);

    if (!receipt) return null;
    const {
        kind = 'cashout',        // 'cashout' | 'deposit'
        gross = 0,
        fee = 0,
        net = gross,
        method = '',
        handle = '',
        etaHours = null,
        afterHours = false,
        date = new Date().toISOString(),
        reference = '',
    } = receipt;

    const title = kind === 'deposit' ? 'DEPOSIT RECEIPT' : 'PAYOUT RECEIPT';
    const bigLabel = kind === 'deposit' ? 'Amount loaded' : 'You will receive';
    const dateStr = (() => {
        try {
            return new Date(date).toLocaleString('en-US', {
                timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric',
                hour: 'numeric', minute: '2-digit',
            }) + ' CT';
        } catch { return date; }
    })();

    const capture = async () => {
        if (!cardRef.current) return null;
        return toPng(cardRef.current, {
            pixelRatio: 2,
            backgroundColor: '#0b0b0f',
            cacheBust: true,
        });
    };

    const download = async () => {
        setBusy(true);
        try {
            const url = await capture();
            const a = document.createElement('a');
            a.href = url;
            a.download = `betohalics-${kind}-receipt-${reference || Date.now()}.png`;
            a.click();
            toast.success('Receipt saved to your device');
        } catch {
            toast.error('Could not save the receipt — try again');
        } finally { setBusy(false); }
    };

    const share = async () => {
        setBusy(true);
        try {
            const url = await capture();
            const blob = await (await fetch(url)).blob();
            const file = new File([blob], `betohalics-${kind}-receipt.png`, { type: 'image/png' });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: 'Betohalics receipt',
                    text: kind === 'deposit' ? `Loaded ${fmt$(net)} on Betohalics` : `Cashed out ${fmt$(net)} on Betohalics 🏆`,
                });
            } else {
                // Fallback — no file share support, just save it.
                const a = document.createElement('a');
                a.href = url;
                a.download = `betohalics-${kind}-receipt.png`;
                a.click();
                toast.success('Receipt saved — share it from your gallery');
            }
        } catch (e) {
            if (e?.name !== 'AbortError') toast.error('Could not share the receipt');
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                className="bg-[#0b0b0f] border-[#FFD700]/25 max-w-sm p-0 overflow-hidden"
                data-testid="branded-receipt-dialog"
            >
                {/* Captured card */}
                <div ref={cardRef} className="relative px-6 pt-7 pb-6 bg-[#0b0b0f]" data-testid="branded-receipt-card">
                    <div
                        className="pointer-events-none absolute inset-x-0 -top-16 h-40 blur-3xl opacity-40"
                        style={{ background: 'radial-gradient(ellipse at center, rgba(255,215,0,0.35), transparent 70%)' }}
                    />
                    <img
                        src="/betohalics-logo.png"
                        alt="Betohalics"
                        crossOrigin="anonymous"
                        className="relative mx-auto w-auto max-h-14 object-contain"
                        style={{ filter: 'drop-shadow(0 4px 18px rgba(255,215,0,0.35))' }}
                    />
                    <div className="relative mt-4 flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#39FF14]" />
                        <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#FFD700]">{title}</span>
                    </div>

                    <div className="relative mt-5 text-center">
                        <p className="text-[10px] uppercase tracking-widest text-white/45 font-bold">{bigLabel}</p>
                        <p className="text-4xl font-black text-[#39FF14] font-mono tabular-nums mt-1" data-testid="branded-receipt-net">
                            {fmt$(net)}
                        </p>
                        {etaHours != null && kind === 'cashout' && (
                            <p className="text-[11px] text-white/50 mt-1">
                                {afterHours ? 'Processing after 8 AM CT' : `Processing within ${etaHours} hours`}
                            </p>
                        )}
                    </div>

                    <div className="relative mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-3.5 space-y-2 text-[12px]">
                        <Row label={kind === 'deposit' ? 'Loaded' : 'Requested'} value={fmt$(gross)} />
                        {fee > 0 && <Row label="Processing fee (6%)" value={`−${fmt$(fee)}`} valueClass="text-[#FFB84D]" />}
                        <div className="border-t border-white/10 pt-2">
                            <Row label={kind === 'deposit' ? 'Credited' : 'Net payout'} value={fmt$(net)} bold valueClass="text-[#39FF14]" />
                        </div>
                        <div className="border-t border-white/10 pt-2 space-y-2">
                            {method && <Row label="Method" value={method} />}
                            {handle && <Row label="Sent to" value={handle} valueClass="text-[#FFD700] font-mono" />}
                            <Row label="Date" value={dateStr} />
                            <Row label="Reference" value={`#${reference || String(Date.now()).slice(-8)}`} valueClass="font-mono text-white/70" />
                        </div>
                    </div>

                    <p className="relative mt-4 text-center text-[9px] font-bold uppercase tracking-[0.25em] text-[#FFD700]/70">
                        Real Games · Real People · Real Wins
                    </p>
                    <p className="relative mt-1 text-center text-[9px] text-white/30">betohalics.com</p>
                </div>

                {/* Actions (not part of the captured image) */}
                <div className="flex items-center gap-2 px-5 pb-5">
                    <button
                        type="button"
                        onClick={download}
                        disabled={busy}
                        className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 font-black text-sm bg-[#FFD700] text-black hover:bg-[#FFE55C] disabled:opacity-60 transition-colors"
                        data-testid="branded-receipt-save"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Save
                    </button>
                    <button
                        type="button"
                        onClick={share}
                        disabled={busy}
                        className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 font-black text-sm bg-[#39FF14] text-black hover:bg-[#5cff3a] disabled:opacity-60 transition-colors"
                        data-testid="branded-receipt-share"
                    >
                        <Share2 className="w-4 h-4" /> Share
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl p-3 text-white/50 hover:bg-white/10 transition-colors"
                        data-testid="branded-receipt-close"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const Row = ({ label, value, bold = false, valueClass = 'text-white' }) => (
    <div className="flex items-center justify-between gap-3">
        <span className="text-white/50">{label}</span>
        <span className={`text-right tabular-nums ${bold ? 'font-black' : 'font-semibold'} ${valueClass}`}>{value}</span>
    </div>
);

export default BrandedReceipt;
